import React, { FC, useRef, useCallback, useState } from "react";
import styled, { css } from "styled-components/native";
import {
  font,
  text,
  color,
  spacing,
  boxShadow,
  borderRadius,
  chevron,
  borderWidth,
  wpdColor,
} from "../../styles/theme";
import { TouchableOpacity } from "react-native";
import Text from "../../styles/Text";
import chevronDownIcon from "../../assets/icons/chevron_down.svg";
import chevronDownIconSecondary from "../../assets/icons/chevron_down_secondary.svg";
import { useFocus, useHover } from "react-native-web-hooks";

const DropdownBox = styled.View`
  top: calc(100% + ${spacing.tiny});
  right: 0;
  padding: ${spacing.small};
  background-color: ${wpdColor.backgroundLighter};
  ${boxShadow.extreme};
  border-radius: ${borderRadius.medium};
  position: absolute;
`;

const DropdownContainer = styled.View`
  position: relative;
`;

const BottomSpacer = styled.View`
  position: absolute;
  width: 100%;
  bottom: 0;
  padding-bottom: calc(${spacing.medium} + ${spacing.medium});
  top: 100%;
`;

interface DropdownTextStyleProps {
  border?: boolean;
  isActive?: boolean;
  fixedColor?: string;
}

const DropdownText = styled(Text)<DropdownTextStyleProps>`
  font-family: ${font.defaultMedium};
  font-size: ${text.small.size};
  background-color: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ isActive, fixedColor }) =>
    isActive ? fixedColor ?? color.secondary : fixedColor ?? color.darker};
  ${({ isActive }) =>
    isActive &&
    css`
      font-weight: 900;
    `};
  ${({ border }) =>
    border &&
    css`
      border: ${borderWidth.small} solid ${color.dark};
      border-radius: ${borderRadius.full};
      padding: ${spacing.extraSmall} ${spacing.medium};
    `};
`;

interface IconProps {
  isOpen?: boolean;
  fixedColor?: string;
}

export const Icon = styled.Image<IconProps>`
  width: ${chevron.width};
  height: ${chevron.height};
  margin-left: ${spacing.small};
  ${({ fixedColor }) =>
    fixedColor &&
    css`
      fill: ${fixedColor};
    `};

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotateX(180deg);
    `};
`;

export interface DropdownProps extends DropdownTextStyleProps {
  text: string;
  border?: boolean;
  isOpeningOnHover?: boolean;
  isActive?: boolean;
  isOpen?: boolean;
  dropdownIconUri?: string;
  setIsOpen?: (isOpen: any) => void;
  onPress?: () => void;
}

const Dropdown: FC<DropdownProps> = ({
  children,
  text,
  border,
  isOpeningOnHover,
  isActive,
  setIsOpen: setIsOpenOverride,
  isOpen: isOpenOverride,
  onPress,
  fixedColor,
  dropdownIconUri,
}) => {
  const element = useRef<any>(null);
  const [isOpenDefault, setIsOpenDefault] = useState(false);
  const isHovering = useHover(element);
  const isFocussing = useFocus(element);
  const isOpenByHover = (isHovering || isFocussing) && isOpeningOnHover;
  const setIsOpen = setIsOpenOverride ? setIsOpenOverride : setIsOpenDefault;
  const isOpen = isOpenOverride ? isOpenOverride : isOpenDefault;

  const toggleDropdown = useCallback(
    () => setIsOpen((oldValue) => !oldValue),
    [setIsOpen]
  );
  const onPressFunction = isOpeningOnHover ? () => null : toggleDropdown;

  return (
    <DropdownContainer ref={element}>
      <TouchableOpacity onPress={onPress || onPressFunction}>
        <DropdownText
          isActive={isActive}
          border={border}
          fixedColor={fixedColor}
        >
          {text}
          <Icon
            isOpen={isOpen || isOpenByHover}
            fixedColor={fixedColor}
            source={{
              uri: isActive
                ? dropdownIconUri ?? chevronDownIcon.toString()
                : dropdownIconUri ?? chevronDownIconSecondary.toString(),
            }}
          />
          {isOpenByHover ? <BottomSpacer /> : null}
        </DropdownText>
      </TouchableOpacity>
      {isOpen || isOpenByHover ? (
        <DropdownBox style={{ zIndex: 101 }}>{children}</DropdownBox>
      ) : null}
    </DropdownContainer>
  );
};

export default Dropdown;
